import React from "react";
import { groupBy, map, difference, union } from "lodash-es";
import {
  Icon,
  Box,
  Center,
  Text,
  Wrap,
  WrapItem,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Checkbox,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  useCheckboxGroup,
  useBoolean,
  Switch,
  useColorMode,
  useToken,
} from "@chakra-ui/react";
import { useController } from "react-hook-form";

import { Icons } from "#app/components/index.jsx";

export const GroupedCheckboxButtonGroup = React.memo(
  ({
    name,
    label,
    control,
    options,
    helperText,
    groupBy: groupByFn,
    size = "md",
    isToggleable = false,
    isToggleableDefaultChecked = false,
    isRequired = false,
    isRequiredMessage = "Please select at least one option",
    ...rest
  }) => {
    const {
      field: { value, onChange },
      formState: { errors },
    } = useController({
      control,
      name,
      rules: {
        required: { value: isRequired, message: isRequiredMessage },
      },
    });

    const isInvalid = !!errors[name];

    const [isExpanded, setExpanded] = useBoolean(
      !isToggleable || isToggleableDefaultChecked,
    );

    const checkedSet = React.useMemo(() => new Set(value), [value]);

    const groups = React.useMemo(
      () =>
        map(groupBy(options, groupByFn), (groupOptions, groupName) => {
          const checkedCount = groupOptions.filter((o) =>
            checkedSet.has(o.value),
          ).length;
          return {
            groupName,
            options: groupOptions,
            values: groupOptions.map((o) => o.value),
            isSomeSelected: checkedCount > 0,
            isAllSelected: checkedCount === groupOptions.length,
            checkedCount,
          };
        }),
      [options, groupByFn, checkedSet],
    );

    const defaultAccordionIndex = groups
      .map((group, index) =>
        group.isSomeSelected && !group.isAllSelected ? index : null,
      )
      .filter((g) => g !== null);

    return (
      <FormControl
        as="fieldset"
        isInvalid={isInvalid}
        isRequired={isRequired}
        {...rest}
      >
        <FormLabel
          as="legend"
          htmlFor={name}
          textAlign="inherit"
          display="flex"
          alignItems="center"
        >
          {label}
          {isToggleable && (
            <Switch
              id={name}
              colorScheme="brand"
              ml={2}
              value={isExpanded}
              defaultChecked={isToggleableDefaultChecked}
              onChange={() => {
                setExpanded.toggle();
                onChange([]);
              }}
            />
          )}
        </FormLabel>
        {isExpanded &&
          (groups.length === 0 ? (
            <Text
              borderWidth="1px"
              borderRadius="md"
              px={4}
              py={2}
              fontSize="sm"
            >
              No matching sites. Please broaden your filters.
            </Text>
          ) : (
            <Accordion
              allowMultiple
              defaultIndex={defaultAccordionIndex}
              reduceMotion
            >
              <GroupedCheckboxButtonGroupList
                groups={groups}
                size={size}
                value={value}
                onChange={onChange}
                checkedSet={checkedSet}
              />
            </Accordion>
          ))}
        {errors[name] ? (
          <FormErrorMessage>{errors[name]?.message}</FormErrorMessage>
        ) : (
          <FormHelperText>{helperText}</FormHelperText>
        )}
      </FormControl>
    );
  },
);

const GroupedCheckboxButtonGroupList = ({
  groups,
  onChange,
  value,
  checkedSet,
  size,
}) => {
  const { colorMode } = useColorMode();
  const [brand500] = useToken("colors", ["brand.500"]);

  // Stable refs so the onToggle callback never goes stale
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;
  const valueRef = React.useRef(value);
  valueRef.current = value;

  // Stable callback — identity never changes, reads current state from refs
  const onToggle = React.useCallback((toggleValue) => {
    const current = valueRef.current;
    if (current.includes(toggleValue)) {
      onChangeRef.current(current.filter((v) => v !== toggleValue));
    } else {
      onChangeRef.current([...current, toggleValue]);
    }
  }, []);

  return groups.map((group) => {
    const {
      groupName,
      options: groupOptions,
      values,
      isSomeSelected,
      checkedCount,
    } = group;

    return (
      <AccordionItem key={groupName}>
        <AccordionButton p={2}>
          <Switch
            colorScheme="brand"
            mr={2}
            isChecked={isSomeSelected}
            onChange={() => {
              if (isSomeSelected) {
                onChangeRef.current(difference(valueRef.current, values));
              } else {
                onChangeRef.current(union(valueRef.current, values));
              }
            }}
          />
          <Box flex="1" textAlign="left">
            <Text as="span" mr={1} fontSize="sm">
              {groupName}
            </Text>
            <Text as="span" color="subtle" whiteSpace="nowrap" fontSize="sm">
              (
              <Text as="span" color="accent">
                {checkedCount}
              </Text>{" "}
              of {groupOptions.length})
            </Text>
          </Box>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel pb={4}>
          <Wrap>
            {groupOptions.map(({ label, value, icon }) => {
              return (
                <MemoizedCheckboxButton
                  key={value}
                  size={size}
                  icon={icon}
                  colorMode={colorMode}
                  brand500={brand500}
                  isChecked={checkedSet.has(value)}
                  value={value}
                  onToggle={onToggle}
                >
                  {label}
                </MemoizedCheckboxButton>
              );
            })}
          </Wrap>
        </AccordionPanel>
      </AccordionItem>
    );
  });
};

export function CheckboxButtonGroup({
  name,
  label,
  control,
  options,
  helperText,
  size = "md",
  isToggleable = false,
  isToggleableDefaultChecked = false,
  isRequired = false,
  isRequiredMessage = "Please select at least one option",
  transform = {
    input: (value) => value,
    output: (value) => value,
  },
  ...rest
}) {
  const {
    field: { value, onChange, ref },
    formState: { errors },
  } = useController({
    control,
    name,
    rules: {
      required: { value: isRequired, message: isRequiredMessage },
    },
  });
  const isInvalid = !!errors[name];

  const [isExpanded, setExpanded] = useBoolean(
    !isToggleable || isToggleableDefaultChecked,
  );

  const { colorMode } = useColorMode();
  const [brand500] = useToken("colors", ["brand.500"]);

  const transformedValue = transform.input(value);
  const checkedSet = React.useMemo(
    () => new Set(transformedValue),
    [transformedValue],
  );

  const handleToggle = (toggleValue) => {
    const current = transform.input(value);
    if (current.includes(toggleValue)) {
      onChange(transform.output(current.filter((v) => v !== toggleValue)));
    } else {
      onChange(transform.output([...current, toggleValue]));
    }
  };

  return (
    <FormControl
      as="fieldset"
      isInvalid={isInvalid}
      isRequired={isRequired}
      {...rest}
    >
      <FormLabel
        as="legend"
        htmlFor={name}
        textAlign="inherit"
        display="flex"
        alignItems="center"
      >
        {label}
        {isToggleable && (
          <Switch
            id={name}
            colorScheme="brand"
            ml={2}
            value={isExpanded}
            defaultChecked={isToggleableDefaultChecked}
            onChange={() => {
              setExpanded.toggle();
              onChange(transform.output([]));
            }}
          />
        )}
      </FormLabel>
      {isExpanded && (
        <Wrap>
          {options.map(({ label, value, icon }, i) => {
            return (
              <CheckboxButton
                key={value}
                size={size}
                icon={icon}
                colorMode={colorMode}
                brand500={brand500}
                isChecked={checkedSet.has(value)}
                value={value}
                onToggle={handleToggle}
                ref={i === 0 ? ref : undefined}
              >
                {label}
              </CheckboxButton>
            );
          })}
        </Wrap>
      )}
      {errors[name] ? (
        <FormErrorMessage>{errors[name]?.message}</FormErrorMessage>
      ) : (
        <FormHelperText>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
}

const visuallyHiddenStyle = {
  border: "0px",
  clip: "rect(0px, 0px, 0px, 0px)",
  height: "1px",
  width: "1px",
  margin: "-1px",
  padding: "0px",
  overflow: "hidden",
  whiteSpace: "nowrap",
  position: "absolute",
};

const CheckboxButton = React.forwardRef(
  (
    { size, icon, colorMode, brand500, isChecked, value, onToggle, children },
    ref,
  ) => {
    return (
      <WrapItem as="label">
        <input
          type="checkbox"
          checked={isChecked}
          onChange={() => onToggle(value)}
          value={value}
          ref={ref}
          style={visuallyHiddenStyle}
        />
        <Box
          as={Center}
          minW="31px"
          cursor="pointer"
          data-checked={isChecked ? "" : undefined}
          borderWidth="1px"
          borderRadius="md"
          boxShadow="sm"
          borderColor={colorMode === "light" ? "gray.200" : "whiteAlpha.300 "}
          _checked={{
            bg: colorMode === "light" ? "brand.500" : "brand.200",
            color: colorMode === "light" ? "white" : "gray.900",
            borderColor: colorMode === "light" ? "brand.500" : "whiteAlpha.300",
            _hover: {
              bg: colorMode === "light" ? "brand.600" : "brand.300",
              color: colorMode === "light" ? "white" : "gray.900",
            },
          }}
          sx={{
            "input:focus-visible + &": {
              borderColor: brand500,
              boxShadow: `0 0 0 1px ${brand500}`,
            },
          }}
          _hover={{
            bg:
              colorMode === "light" ? "brand.50" : "rgba(129, 230, 217, 0.12)",
          }}
          px={size === "sm" ? 2 : 3}
          py={size === "sm" ? 1 : 2}
        >
          <Text fontSize={size === "sm" ? "sm" : "md"}>{children}</Text>
          {icon ? (
            <Icon
              as={Icons[icon]}
              fontSize={size === "sm" ? "md" : "xl"}
              ml={2}
            />
          ) : null}
        </Box>
      </WrapItem>
    );
  },
);

const MemoizedCheckboxButton = React.memo(CheckboxButton, (prev, next) => {
  return (
    prev.isChecked === next.isChecked &&
    prev.value === next.value &&
    prev.children === next.children &&
    prev.size === next.size &&
    prev.icon === next.icon &&
    prev.colorMode === next.colorMode &&
    prev.brand500 === next.brand500
  );
});

export function CheckboxGroup({
  name,
  label,
  control,
  options,
  helperText,
  size = "md",
  isToggleable = false,
  isToggleableDefaultChecked = false,
  isRequired = false,
  isRequiredMessage = "Please select at least one option",
  transform = {
    input: (value) => value,
    output: (value) => value,
  },
  ...rest
}) {
  const {
    field: { value, onChange, ref },
    formState: { errors },
  } = useController({
    control,
    name,
    rules: {
      required: { value: isRequired, message: isRequiredMessage },
    },
  });
  const isInvalid = !!errors[name];

  const { getCheckboxProps } = useCheckboxGroup({
    value: transform.input(value),
    onChange: (v) => onChange(transform.output(v)),
  });

  const [isExpanded, setExpanded] = useBoolean(
    !isToggleable || isToggleableDefaultChecked,
  );

  return (
    <FormControl
      as="fieldset"
      isInvalid={isInvalid}
      isRequired={isRequired}
      {...rest}
    >
      <FormLabel
        as="legend"
        htmlFor={name}
        textAlign="inherit"
        display="flex"
        alignItems="center"
        mb={4}
      >
        {label}
        {isToggleable && (
          <Switch
            id={name}
            colorScheme="brand"
            ml={2}
            value={isExpanded}
            defaultChecked={isToggleableDefaultChecked}
            onChange={() => {
              setExpanded.toggle();
              onChange(transform.output([]));
            }}
          />
        )}
      </FormLabel>
      {isExpanded && (
        <Wrap spacing={2}>
          {options.map(({ label, value }, i) => {
            const checkbox = getCheckboxProps({ value });
            return (
              <Checkbox
                key={value}
                size={size}
                {...checkbox}
                ref={i === 0 ? ref : undefined}
              >
                {label}
              </Checkbox>
            );
          })}
        </Wrap>
      )}
      {errors[name] ? (
        <FormErrorMessage>{errors[name]?.message}</FormErrorMessage>
      ) : (
        <FormHelperText>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
}
